// 「刪除已完成的批次批改紀錄」的純邏輯(kanban 106957 補做)。
//
// 老師要重新批改某位學生時,國中的動線是「先刪舊紀錄再重新上傳」——
// 批次後端以 (classID, compositionID, seatNo) 判定重複,舊紀錄還在就靜默忽略
// 新的上傳。高中移植時漏掉刪除入口,老師重傳同一位學生毫無反應,這裡補上。
//
// 只放判定規則,不碰 DOM 也不打 API — 方便 vitest(node)直測。

/**
 * 這筆 classrecord 是不是「老師批次上傳」的 → 決定刪除時的「分流」。
 * 批次後端寫回高中 classrecord 時 studentID 一律是 `batch_` 開頭的合成值,
 * 且該筆 _id 同時是 batch-results 的 batchResultID;學生／老師自行上傳的
 * studentID 是 OIDC sub,沒有對應的 batchResult。
 * 用途(kanban 106957 放寬後):
 *   - batch_  → 先刪 classrecord 再刪 batch-results,兩段;
 *   - 自行上傳 → 只刪 classrecord 一段(_id 不是 batchResultID,打 batch API 會
 *     404 或誤刪)。
 * 「給不給刪」已改由 canDeleteClassRecord 決定(不分來源),這支只管走哪條。
 * 必須是「開頭」相等:`stu_batch_01` 這種含 batch_ 但不在開頭的是學生資料,
 * 用 includes 判就會把它誤當批次紀錄、多打一次 batch API。
 * 欄位缺值/非字串(舊資料)一律回 false,且不得丟例外。
 */
export const isBatchUploadedRecord = (studentID: unknown): boolean =>
  typeof studentID === "string" && studentID.startsWith("batch_");

/**
 * 刪除鈕顯示條件:已完成(非批改中)的 classrecord 一律給刪 —— 不分來源,
 * 老師批次上傳、學生自行上傳、老師本人自行上傳都給(kanban 106957 第 1 項)。
 * 後端已放寬成「只驗該筆紀錄的班導、不排除本人」,前端顯示條件跟著放寬。
 * 批改中／處理中的紀錄不給刪(與國中 batch-records「非批改中即可刪」對齊):
 * classRecordsData 內本就是後端寫回的完成紀錄,這裡再以 status 擋一層,避免
 * 「先寫回 classrecord → 才標 finish」窗口期的中間態被誤放。
 * status 缺值(舊資料沒有這個欄位)視為已完成、可刪。
 */
const IN_PROGRESS_STATUSES = new Set([
  "pending",
  "processing",
  "grading",
  "running",
  "open",
]);

export const canDeleteClassRecord = (
  record: { status?: string } | null | undefined
): boolean => {
  const status = record?.status;
  return typeof status !== "string" || !IN_PROGRESS_STATUSES.has(status);
};

/**
 * 刪除分流步驟:批次紀錄兩段(先 classrecord 再 batch-results),
 * 自行上傳一段(只 classrecord)。handler 直接照這份清單走,測試守著它,
 * 確保非批次紀錄不會多打 batch API。
 */
export type ClassRecordDeleteStep = "classrecord" | "batchResult";

export const classRecordDeleteSteps = (
  studentID: unknown
): ClassRecordDeleteStep[] =>
  isBatchUploadedRecord(studentID)
    ? ["classrecord", "batchResult"]
    : ["classrecord"];

/**
 * classrecord DELETE 的失敗歸類:
 * - gone(404):紀錄不存在 —— 連點兩次或已被他人刪除,目的已達成,視同成功
 * - forbidden(403):非本班老師/非 batch_ 紀錄/token 錯 —— 紀錄還在,必須中止,
 *   絕不可接著刪 batch-results(刪了就是「畫面還在、卻能重複上傳」的不一致)
 * - error:其餘(422 缺參數、5xx、網路斷)—— 一樣當紀錄還在,中止
 */
export type RecordDeleteFailure = "gone" | "forbidden" | "error";

export const classifyRecordDeleteFailure = (
  error: unknown
): RecordDeleteFailure => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) return "gone";
  if (status === 403) return "forbidden";
  return "error";
};
